import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import leftBackgroundImage from '/img/project_authSideImage.jpg';

/* -------- Redux -------- */
import { useDispatch, useSelector } from "react-redux";
import { loginUser, fetchParameters, clearAuthError, resendActivationEmailRequest, clearUserErrors, clearUserSuccess, sendEmailAdminRequest } from "../../store/actions";
/* ------------------------- */

/* -------- packages -------- */
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import {
  Row,
  Col,
  CardBody,
  Card,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
  InputGroup,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
} from "reactstrap";
/* ------------------------- */

/* -------- Intern functions -------- */
import { getErrorFromResponse } from "../../utils/utils";
import { persistor } from "../../store";
import ErrorHandler from "../../components/ui/Error/ErrorHandler";
/* ------------------------- */

export default function Login() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const parameters = useSelector(state => state.Parameters);
  document.title = `${t('login')} | ${parameters.appName}`;
  
  const { user, errorAuth, loadingAuth } = useSelector(state => state.Login);
  const { resendLoading, resendSuccess, resendError, sendAdminLoading, sendAdminSuccess, sendAdminError } = useSelector(state => state.Users);
  const token = localStorage.getItem('accessToken');

  useEffect(() => {
    if (token) {
      persistor.purge().then(() => {
        navigate("/logout");
      });
    }
    
    if (user && user?.id) {      
      persistor.purge().then(() => {
        navigate("/logout");
      });
    }
  }, [navigate]);

  useEffect(() => {
    if (!parameters.logoIcon || !parameters.logo) {
      fetch('/parameters.json')
        .then(response => response.json())
        .then((data) => {
          if(data) {
            dispatch(fetchParameters(data));
          }
        })
        .catch((error) => {
          console.error('Error loading parameters:', error);
        });
    }
  }, [dispatch]);

  useEffect(() => {
    if (user?.id) {
      if(user?.groupId == 1 || user?.groupId == 2) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/projects', { replace: true });
      }
    }
  }, [user, navigate]);

  // Gérer le succès du renvoi d'email
  useEffect(() => {
    if (resendSuccess) {
      const timer = setTimeout(() => {
        dispatch(clearUserSuccess());
        toggleModalEmailNotReceived();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [resendSuccess]);

  // Gérer l'erreur du renvoi d'email
  useEffect(() => {
    if (resendError) {
      const timer = setTimeout(() => {
        dispatch(clearUserErrors());
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [resendError]);

  // Gérer le succès de l'envoi du message admin
  useEffect(() => {
    if (sendAdminSuccess) {
      setMessageSent(true);
      const timer = setTimeout(() => {
        dispatch(clearUserSuccess());
        toggleModalEmailNotReceived();
        setMessageSent(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [sendAdminSuccess]);

  // Gérer l'erreur de l'envoi du message admin
  useEffect(() => {
    if (sendAdminError) {
      const timer = setTimeout(() => {
        dispatch(clearUserErrors());
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [sendAdminError]);

  const [showPassword, setShowPassword] = useState(false);
  const [modalEmailNotReceived, setModalEmailNotReceived] = useState(false);
  const [requestNewEmail, setRequestNewEmail] = useState(false);
  const [contactAdmin, setContactAdmin] = useState(false);
  const [messageSent, setMessageSent] = useState(false);

  const signInForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email(t('EMAIL')).required(t('errorEnterEmail')),
      password: Yup.string().required(t('errorEnterPassword')),
    }),
    onSubmit: (values) => {
      dispatch(loginUser(values, navigate));
    },
  });

  // Formulaire pour la modal emailNotReceived
  const emailNotReceivedForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      newEmail: '',
      adminEmail: '',
      adminMessage: '',
    },
    validationSchema: Yup.object({
      newEmail: requestNewEmail ? Yup.string().email(t('EMAIL')).required(t('errorEnterEmail')) : Yup.string(),
      adminEmail: contactAdmin ? Yup.string().email(t('EMAIL')).required(t('errorEnterEmail')) : Yup.string(),
      adminMessage: contactAdmin ? Yup.string().required(t('errorEnterMessage')) : Yup.string(),
    }),
    onSubmit: (values) => {
      if (requestNewEmail) {
        dispatch(resendActivationEmailRequest(values.newEmail));
      }
      
      if (contactAdmin) {
        dispatch(sendEmailAdminRequest({email: values.adminEmail, message: values.adminMessage}));
      }
    },
  });

  // Gestion des erreurs spécifiques aux champs (comme dans DynamicForm)
  useEffect(() => {
    if (errorAuth && errorAuth.data && errorAuth.data.errors) {
      errorAuth.data.errors.forEach(err => {
        if (err.code && err.key) {
          signInForm.setFieldError(err.key, t(err.code));
        }
      });
    }
  }, [errorAuth]);

  const handleSubmit = (e) => {
    e.preventDefault();
    signInForm.handleSubmit(e);
  };

  const toggleModalEmailNotReceived = () => {
    setModalEmailNotReceived(!modalEmailNotReceived);
    if (!modalEmailNotReceived) {
      // Reset du formulaire et des états à l'ouverture
      emailNotReceivedForm.resetForm();
      setRequestNewEmail(false);
      setContactAdmin(false);
      setMessageSent(false);
    }
  };

  const handleEmailNotReceivedSubmit = (e) => {
    e.preventDefault();
    emailNotReceivedForm.handleSubmit(e);
  };

  ErrorHandler(errorAuth, clearAuthError);

  if (!parameters.logoIcon) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 authenticationPage">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="p-0 m-0 vh-100 overflow-auto d-flex flex-column authenticationPage">
      <Row className="h-100 m-0">
        <Col lg={8} className="d-none d-lg-block p-0">
          <div className="auth-full-bg p-0 h-100">
            <div className="d-flex h-100 p-0">
              <img src={leftBackgroundImage} alt="" className="img-fluid" />
              <span className="logoLogin">
                <img src={parameters.logoDark} alt="logo" width={300} className="logo"/>
              </span>
            </div>
          </div>
        </Col>

        <Col lg={4} className="p-0 authenticationContainer h-100 d-flex flex-column justify-content-between flex-grow-1">
          <Row className="d-flex flex-column justify-content-center align-items-center h-25">
            <Col className="cardHeader mg-0 signIn">
              <Col lg={12} className="col p-0">
                <div className="text-primary">
                  <h5 className="text-primary">{t('welcomeBack')}</h5>
                  <p>{t('loginText') + ' ' + parameters.appName}</p>
                </div>
              </Col>
            </Col>
          </Row>
          
          <Row className="d-flex flex-column align-items-center p-3 h-75">
            <Card className="overflow-hidden">
              <CardBody className="pt-0 interactionContainer">
                <Form className="form-horizontal signIn" onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <Label id="projUsernameLabel" className="form-label">{t('email')}</Label>
                    <Input
                      name="email"
                      className="form-control"
                      type="text"
                      placeholder={parameters.appName + '@example.com'}
                      onChange={signInForm.handleChange}
                      onBlur={signInForm.handleBlur}
                      value={signInForm.values.email}
                      invalid={signInForm.touched.email && signInForm.errors.email ? true : false}
                      data-cy="signin-email"
                    />
                    {signInForm.touched.email && signInForm.errors.email ? (
                      <FormFeedback type="invalid" id="projFormFeedbackemail" data-cy="signin-emailError">
                        {signInForm.errors.email}
                      </FormFeedback>
                    ) : null}
                  </div>

                  <div className="mb-3">
                    <Label id="projPasswordLabel" className="form-label">{t('password')}</Label>
                    <InputGroup>
                      <Input
                        name="password"
                        className="input-password"
                        placeholder="example"
                        autoComplete="on"
                        value={signInForm.values.password}
                        type={showPassword ? "text" : "password"}
                        onChange={signInForm.handleChange}
                        onBlur={signInForm.handleBlur}
                        invalid={signInForm.touched.password && signInForm.errors.password ? true : false}
                        data-cy="signin-password"
                      />
                      {signInForm.touched.password && signInForm.errors.password ? (
                        <FormFeedback type="invalid" id="projFormFeedbackPassword" data-cy="signin-passwordError">
                          {signInForm.errors.password}
                        </FormFeedback>
                      ) : null}
                      {!(signInForm.touched.password && signInForm.errors.password) && (
                        <span className='passwordVisibility' onClick={() => setShowPassword(!showPassword)} data-cy="signin-passwordVisibility">
                          <a id="projTogglePasswordVisibility">
                            <i className={showPassword ? 'mdi mdi-eye' : 'mdi mdi-eye-off'} />
                          </a>
                        </span>
                      )}
                    </InputGroup>
                  </div>
                  
                  <div className="mt-3 d-grid">
                    <button className="btn btn-primary btn-block" type="submit" data-cy="signin-submit">
                      {loadingAuth ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                        </>
                      ) : (
                        t('login')
                      )}
                    </button>
                    <button 
                      className="btn btn-link lostPassword" 
                      type="button" 
                      onClick={() => navigate('/forgotPassword')}
                    >
                      {t('lostPassword')}
                    </button>
                    <button 
                      className="btn btn-link emailNotReceived" 
                      type="button" 
                      onClick={toggleModalEmailNotReceived}
                    >
                      {t('emailNotReceived')}
                    </button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Row>

          <Row className="h-25">
            <div className="cardFooter">
              <div className="textArea">
                <p className="registerTitle">{t('registerTitle') + ' ' + parameters.appName + '?'}</p>
                <p>{t('registerText')}</p>
              </div>
              <button 
                className="btn btn-primary btn-block" 
                type="button"
                onClick={() => navigate('/register')}
                data-cy="signup-button"
              >
                {t('register')}
              </button>
            </div>
          </Row>
        </Col>
      </Row>

      {/* Modal Email Non Reçu */}
      <Modal 
        isOpen={modalEmailNotReceived} 
        toggle={toggleModalEmailNotReceived}
        centered
      >
        <ModalHeader toggle={toggleModalEmailNotReceived}>
          {t('emailNotReceived')}
        </ModalHeader>
        <ModalBody style={{minHeight: '440px'}}>
          {/* Loading state pour le renvoi d'email */}
          {resendLoading && requestNewEmail && !contactAdmin ? (
            <div className="text-center py-4">
              <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
              <p className="mt-3 mb-0">{t('sendingEmail')}</p>
            </div>
          ) : /* Success state pour le renvoi d'email */
          resendSuccess && !contactAdmin ? (
            <div className="text-center py-4">
              <i className="mdi mdi-check-circle text-success" style={{ fontSize: '48px' }}></i>
              <p className="mt-3 mb-0">{t('emailResent')}</p>
            </div>
          ) : /* Error state pour le renvoi d'email */
          resendError && !contactAdmin ? (
            <div className="text-center py-4">
              <i className="mdi mdi-close-circle text-danger" style={{ fontSize: '48px' }}></i>
              <p className="mt-3 mb-0">{(resendError?.data?.errors != null && resendError?.data?.errors[0]?.code == "UNKNOWN_PARAMETER" && resendError?.data?.errors[0]?.key == "email")?t('emailDoesNotExist'):t('emailResendError')}</p>
            </div>
          ) : /* Loading state pour le message admin */
          sendAdminLoading && contactAdmin && !requestNewEmail ? (
            <div className="text-center py-4">
              <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
              <p className="mt-3 mb-0">{t('sendingMessage')}</p>
            </div>
          ) : /* Success state pour le message admin */
          sendAdminSuccess && contactAdmin ? (
            <div className="text-center py-4">
              <i className="mdi mdi-check-circle text-success" style={{ fontSize: '48px' }}></i>
              <p className="mt-3 mb-0">{t('messageSentToAdmin')}</p>
            </div>
          ) : /* Error state pour le message admin */
          sendAdminError && contactAdmin ? (
            <div className="text-center py-4">
              <i className="mdi mdi-close-circle text-danger" style={{ fontSize: '48px' }}></i>
              <p className="mt-3 mb-0">{(sendAdminError?.data?.errors != null && sendAdminError?.data?.errors[0]?.code == "UNKNOWN_PARAMETER" && sendAdminError?.data?.errors[0]?.key == "email")?t('emailDoesNotExist'):t('messageSendError')}</p>
            </div>
          ) : /* Formulaire par défaut */
          (
            <>
              <p className="mb-4">
                {t('emailNotReceivedExplanation')}
              </p>
              
              <Form onSubmit={handleEmailNotReceivedSubmit}>
                {/* Option 1 : Demander un nouvel email */}
                <FormGroup check className="mb-3">
                  <Label check>
                    <Input 
                      type="radio" name="emailNotReceivedOption" 
                      checked={requestNewEmail}
                      onChange={(e) => { if (e.target.checked) { setRequestNewEmail(true); setContactAdmin(false); } }}
                    />
                    {' '}{t('requestNewEmail')}
                  </Label>
                </FormGroup>

                {requestNewEmail && (
                  <div className="mb-3 ms-4">
                    <Label className="form-label">{t('email')}</Label>
                    <Input
                      name="newEmail"
                      type="email"
                      placeholder={t('emailPlaceholder')}
                      onChange={emailNotReceivedForm.handleChange}
                      onBlur={emailNotReceivedForm.handleBlur}
                      value={emailNotReceivedForm.values.newEmail}
                      invalid={
                        emailNotReceivedForm.touched.newEmail && 
                        emailNotReceivedForm.errors.newEmail ? true : false
                      }
                    />
                    {emailNotReceivedForm.touched.newEmail && emailNotReceivedForm.errors.newEmail ? (
                      <FormFeedback type="invalid">
                        {emailNotReceivedForm.errors.newEmail}
                      </FormFeedback>
                    ) : null}
                  </div>
                )}

                {/* Option 2 : Contacter un administrateur */}
                <FormGroup check className="mb-3">
                  <Label check>
                    <Input 
                      type="radio" name="emailNotReceivedOption" 
                      checked={contactAdmin}
                      onChange={(e) => { if (e.target.checked) { setContactAdmin(true); setRequestNewEmail(false); } }}
                    />
                    {' '}{t('contactAdministrator')}
                  </Label>
                </FormGroup>

                {contactAdmin && (
                  <div className="ms-4">
                    <div className="mb-3">
                      <Label className="form-label">{t('email')}</Label>
                      <Input
                        name="adminEmail"
                        type="email"
                        placeholder={t('emailPlaceholder')}
                        onChange={emailNotReceivedForm.handleChange}
                        onBlur={emailNotReceivedForm.handleBlur}
                        value={emailNotReceivedForm.values.adminEmail}
                        invalid={
                          emailNotReceivedForm.touched.adminEmail && 
                          emailNotReceivedForm.errors.adminEmail ? true : false
                        }
                      />
                      {emailNotReceivedForm.touched.adminEmail && emailNotReceivedForm.errors.adminEmail ? (
                        <FormFeedback type="invalid">
                          {emailNotReceivedForm.errors.adminEmail}
                        </FormFeedback>
                      ) : null}
                    </div>

                    <div className="mb-3">
                      <Label className="form-label">{t('message')}</Label>
                      <Input
                        name="adminMessage"
                        type="textarea"
                        rows="4"
                        placeholder={t('messagePlaceholder')}
                        onChange={emailNotReceivedForm.handleChange}
                        onBlur={emailNotReceivedForm.handleBlur}
                        value={emailNotReceivedForm.values.adminMessage}
                        invalid={
                          emailNotReceivedForm.touched.adminMessage && 
                          emailNotReceivedForm.errors.adminMessage ? true : false
                        }
                      />
                      {emailNotReceivedForm.touched.adminMessage && emailNotReceivedForm.errors.adminMessage ? (
                        <FormFeedback type="invalid">
                          {emailNotReceivedForm.errors.adminMessage}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </div>
                )}

                {(requestNewEmail || contactAdmin) && (
                  <div className="mt-4 d-grid">
                    <button 
                      className="btn btn-primary" 
                      type="submit"
                    >
                      {requestNewEmail && !contactAdmin && t('receiveNewEmail')}
                      {!requestNewEmail && contactAdmin && t('send')}
                      {requestNewEmail && contactAdmin && t('validate')}
                    </button>
                  </div>
                )}
              </Form>
            </>
          )}
        </ModalBody>
        {!resendLoading && !resendSuccess && !resendError && !sendAdminLoading && !sendAdminSuccess && !sendAdminError && !messageSent && (
          <ModalFooter>
            <button 
              className="btn btn-secondary" 
              onClick={toggleModalEmailNotReceived}
            >
              {t('close')}
            </button>
          </ModalFooter>
        )}
      </Modal>

      <ToastContainer />
    </Container>
  );
}